import { useMediaQuery, useTheme } from "@wayflyer/flyui";
import { getLegend } from "@wayflyer/flyui-dataviz";
import * as React from "react";
import { useEffect, useState } from "react";
import { roundToNumDecimals } from "shared-utils";
// eslint-disable-next-line no-restricted-imports
import moment from "moment";
import { useIntl } from "react-intl";
import { useLoaderData } from "react-router";
import { Tooltip, type TooltipContentProps } from "recharts";

import { MetricTypes } from "../../autogen/metrics";
import { CurrencyProvider } from "../../currency";
import { type InsightsLoaderReturn } from "../../loader";
import { ltvCacTableColumnTooltips } from "../../strings/tooltips";
import { formatValue } from "../../strings/type-formatting";
import { getUrls } from "../../urls";
import { getDashboardOutput } from "../../utils/fetch-api";
import { useMount } from "../../utils/lifecycle-hooks";
import { TemporalResolutions } from "../../utils/types";

import { useChurn } from "./churn";
import { ConsumersScreen, type ConsumersScreenProps } from "./components/consumers-screen";
import { getBars, getLines, getTooltip } from "./components/consumers-summary-graph/helpers";
import { type ConsumersSummaryTabProps } from "./components/consumers-summary-tab";
import { LTV_CAC_SUMMARY, LTV_METRICS, TemporalResOptions, TempRes } from "./constants";
import { getRange, getRowStyle, isNarrowCurrency, sortRowsDefault } from "./helpers";
import {
  generateSummaryPanels,
  getDataTooltipFn,
  getFullFromShortDate,
  getLtvCacData,
  getNumMonthMap,
  getStyleForData,
  getSummaryData,
  summaryChartPanelProjectedMetrics,
  type ProcessedLtvCacData,
  type ShownMetrics,
} from "./metrics-ltv";

const Consumers = ({ dashboardDataPath }: { dashboardDataPath: string }) => {
  const theme = useTheme();
  const intl = useIntl();
  const showChurnTab = false; // Previously used Optimizely feature flag: show_churn_tab
  const showLtvSummary = false; // Previously used Optimizely feature flag: show_ltv_summary
  const showLtvSummaryProjected = false; // Previously used Optimizely feature flag: show_ltv_summary_projected
  const enableStickyColumns = false; // Previously used Optimizely feature flag: enable_sticky_columns

  const [margin, setMargin] = React.useState("20");
  const [monthsProf, setMonthsProf] = React.useState("6");

  const [selectedTempResIndex, setSelectedTempResIndex] = React.useState(0);
  const absoluteStart = moment().subtract(2, "year");
  const absoluteEnd = moment().subtract(1, "day"); // All available data

  const [dataError, setDataError] = React.useState<string>(null);
  const [startDateString, setStartDateString] = React.useState<string>(null);
  const [endDateString, setEndDateString] = React.useState<string>(null);

  const [ltvCacData, setLtvCacData] = React.useState<ProcessedLtvCacData>(null);
  const [ltvAveragesMonthly, setLtvAveragesMonthly] = React.useState(null);
  const [currencyCode, setCurrencyCode] = React.useState(null);
  const [showProjectedData, setShowProjectedData] = React.useState<boolean>(true);
  const [needsUpdate, setNeedsUpdate] = React.useState<boolean>(true);
  const [consumerProps, setConsumerProps] = React.useState<ConsumersScreenProps>(null);

  const [summaryLTVData, setSummaryLTVData] = React.useState(null);
  const [summaryPanelData, setSummaryPanelData] = React.useState(null);
  const [shownMetrics, setShownMetrics] = useState<ShownMetrics>({
    [MetricTypes.ALLTIME_AOV]: true,
    [MetricTypes.ALLTIME_CAC]: true,
    [MetricTypes.THREE_MONTH_LTV]: true,
    [MetricTypes.SIX_MONTH_LTV]: true,
    [MetricTypes.TWELVE_MONTH_LTV]: true,
  });

  const isMonthly = (tmpResIndex: number) => {
    const tempRes = TemporalResOptions[tmpResIndex].id;
    return tempRes === TempRes.Month;
  };

  const handleMarginChange = (event) => {
    if (!isNaN(event.target.value)) {
      setMargin(event.target.value);
      setNeedsUpdate(true);
    }
  };
  const handleMonthsChange = (event) => {
    if (!isNaN(event.target.value) && (event.target.value === "" || Number.isInteger(parseFloat(event.target.value)))) {
      setMonthsProf(event.target.value);
      setNeedsUpdate(true);
    }
  };

  const handleShowMetricChart = (metricType: string, selected: boolean) => {
    setShownMetrics((shownMetrics) => ({ ...shownMetrics, [metricType]: selected }));
  };

  const latestAvailableDate = (() => {
    const monthly = isMonthly(selectedTempResIndex);
    const endOfSelectedPeriod = moment(endDateString, "YYYY - M").endOf(monthly ? "month" : "quarter");
    const yesterday = moment().subtract(1, "day");
    return yesterday.isSameOrBefore(endOfSelectedPeriod)
      ? yesterday.format("YYYY-MM-DD")
      : endOfSelectedPeriod.format("YYYY-MM-DD");
  })();

  const fetchLTVData = React.useCallback(
    (tmpResIndex: number) => {
      const monthly = isMonthly(tmpResIndex);

      const start = moment(startDateString, "YYYY - M");
      const end = moment(latestAvailableDate, "YYYY-MM-DD");

      getDashboardOutput(
        dashboardDataPath,
        [
          {
            metrics: LTV_METRICS,
            filename: "ltv_cac_daily_insights",
            groupBys: [],
          },
        ],
        { start, end },
        (data) => {
          setCurrencyCode(data.currencyCode);

          const ltvData = getLtvCacData(intl, data, monthly, start, end);

          if (monthly) {
            setLtvAveragesMonthly(ltvData.ltvAverages);
          }
          setLtvCacData(ltvData);
          setSummaryPanelData(generateSummaryPanels(intl, data.currencyCode, data.summary, monthly));
          setNeedsUpdate(true);
        },
        null,
        (error) => {
          setDataError(error);
        },
        monthly ? TemporalResolutions.MONTHLY : TemporalResolutions.QUARTERLY
      );
    },
    [startDateString, latestAvailableDate, dashboardDataPath, intl]
  );
  const fetchLTVSummaryData = React.useCallback(
    (tmpResIndex: number) => {
      const monthly = isMonthly(tmpResIndex);

      const startData = moment().subtract(25, "months"); // Retrieves max data available (2yrs)
      const endData = moment(latestAvailableDate, "YYYY-MM-DD");

      const startChart = getFullFromShortDate(startDateString);
      const endChart = latestAvailableDate;

      getDashboardOutput(
        dashboardDataPath,
        [
          {
            metrics: LTV_METRICS,
            filename: "ltv_cac_daily_insights",
            groupBys: [],
          },
        ],
        { start: startData, end: endData },
        (data) => {
          setCurrencyCode(data.currencyCode);
          const summaryProps = getSummaryData(data, { start: startChart, end: endChart }, shownMetrics, monthly);

          setSummaryLTVData(summaryProps);
          setNeedsUpdate(true);
        },
        null,
        (error) => {
          setDataError(error);
        },
        monthly ? TemporalResolutions.MONTHLY : TemporalResolutions.QUARTERLY
      );
    },
    [latestAvailableDate, startDateString, dashboardDataPath, shownMetrics]
  );

  const resetTimeRange = () => {
    const selectedRangeStart = moment().subtract(12, "month");
    setStartDateString(selectedRangeStart.format("YYYY - M"));
    setEndDateString(absoluteEnd.format("YYYY - M"));
  };

  const handleTempResChange = (tempResObj: string) => {
    const newIndex = TemporalResOptions.findIndex((option) => option.id === tempResObj);
    setLtvCacData(null);
    setSelectedTempResIndex(newIndex);
    resetTimeRange();
  };

  useEffect(() => {
    if (startDateString && endDateString) {
      fetchLTVData(selectedTempResIndex);
      fetchLTVSummaryData(selectedTempResIndex);
    }
  }, [startDateString, endDateString, selectedTempResIndex, fetchLTVData, fetchLTVSummaryData]);

  useMount(() => {
    resetTimeRange();
  });

  const getHeaderProps = (earliestMonth: number, earliestYear: number) => {
    const handleStartDateStringChange = (id: string) => {
      setStartDateString(id);
    };

    const handleEndDateStringChange = (id: string) => {
      setEndDateString(id);
    };
    const monthly = isMonthly(selectedTempResIndex);
    const dateRangeOptionsStart = getRange(intl, earliestMonth, earliestYear, endDateString, true, monthly);
    const dateRangeOptionsEnd = getRange(intl, earliestMonth, earliestYear, startDateString, false, monthly);
    return {
      temporalResOptions: TemporalResOptions,
      selectedTempResIndex,
      onClickTemporalRes: handleTempResChange,
      startDatePicker: {
        currentDateRange: startDateString,
        onChange: handleStartDateStringChange,
        dateRangeOptions: dateRangeOptionsStart,
      },
      endDatePicker: {
        currentDateRange: endDateString,
        onChange: handleEndDateStringChange,
        dateRangeOptions: dateRangeOptionsEnd,
      },
    };
  };
  const query = useMediaQuery();

  const handleSetProjectedSwitch = (isOn: boolean) => {
    setShowProjectedData(isOn);
    setNeedsUpdate(true);
  };

  const generateLTVSummaryChartData = (ff_show_projected: boolean): ConsumersSummaryTabProps => {
    const lines = [];
    const bars = [];
    const monthly = isMonthly(selectedTempResIndex);
    if (shownMetrics[MetricTypes.ALLTIME_AOV]) {
      lines.push({
        datumName: MetricTypes.ALLTIME_AOV,
        name: "AOV",
        color: theme.palette.dataviz.categorical.category4,
      });
    }
    if (shownMetrics[MetricTypes.ALLTIME_CAC]) {
      lines.push({
        datumName: MetricTypes.ALLTIME_CAC,
        name: "CAC",
        color: theme.palette.dataviz.categorical.category3,
      });
    }
    if (shownMetrics[MetricTypes.THREE_MONTH_LTV]) {
      bars.push({
        datumName: MetricTypes.THREE_MONTH_LTV,
        name: `${monthly ? "3M" : "1Q"} Customers Value`,
        stackId: "stack",
        color: theme.palette.dataviz.sequential.viridis[300],
      });
      if (showProjectedData && ff_show_projected) {
        bars.push({
          datumName: MetricTypes.FOUR_MONTH_LTV,
          name: `Projected ${monthly ? "3M" : "1Q"} Customers Value`,
          stackId: "stack",
          color: theme.palette.dataviz.sequential.viridis[300],
          mask: "candy",
        });
      }
    }
    if (shownMetrics[MetricTypes.SIX_MONTH_LTV]) {
      bars.push({
        datumName: MetricTypes.SIX_MONTH_LTV,
        name: `${monthly ? "6M" : "2Q"} Customers Value`,
        stackId: "stack",
        color: theme.palette.dataviz.categorical.category6,
      });
      if (showProjectedData && ff_show_projected) {
        bars.push({
          datumName: MetricTypes.SEVEN_MONTH_LTV,
          name: `Projected ${monthly ? "6M" : "2Q"} Customers Value`,
          stackId: "stack",
          color: theme.palette.dataviz.categorical.category6,
          mask: "candy",
        });
      }
    }
    if (shownMetrics[MetricTypes.TWELVE_MONTH_LTV]) {
      bars.push({
        datumName: MetricTypes.TWELVE_MONTH_LTV,
        name: `${monthly ? "12M" : "4Q"} Customers Value`,
        stackId: "stack",
        color: theme.palette.dataviz.sequential.viridis[700],
        roundCorners: true,
      });
      if (showProjectedData && ff_show_projected) {
        bars.push({
          datumName: MetricTypes.THIRTEEN_MONTH_LTV,
          name: `Projected ${monthly ? "12M" : "4Q"} Customers Value`,
          stackId: "stack",
          color: theme.palette.dataviz.sequential.viridis[700],
          mask: "candy",
          roundCorners: true,
        });
      }
    }

    const makeLegendItems = (lines, bars) => {
      return lines
        .map((line) => {
          return {
            label: line.name,
            color: line.color,
          };
        })
        .concat(
          bars.map((bar) => {
            return {
              label: bar.name,
              color: bar.color,
              dashed: summaryChartPanelProjectedMetrics.includes(bar.datumName),
              isBar: true,
            };
          })
        );
    };

    const makeTooltip = () => {
      return (
        <Tooltip
          content={({ active, payload, label }: TooltipContentProps) => {
            return getTooltip({
              active,
              payload,
              label,
              formatValue: summaryLTVData.formatValue,
              customTooltipPayloadData: summaryLTVData.customToolTipData,
            });
          }}
        />
      );
    };

    const chartData = {
      data: summaryLTVData.data,
      chartFilters: ff_show_projected
        ? {
            switchOneLabel: intl.formatMessage({
              id: "Consumers: Handle projected toggle",
              defaultMessage: "Enable Projected Data",
            }),
            switchesDisabled: false, // isloading
            onSwitchOne: handleSetProjectedSwitch,
            hideSwitchTwo: true,
          }
        : null,
      legend: getLegend(makeLegendItems(lines, bars)),
      lines: getLines(lines, query("sm").windowGt),
      bars: getBars(bars),
      getTooltip: makeTooltip(),
    };

    const optimalCac = (parseFloat(margin) / 100) * ltvAveragesMonthly[monthsProf];
    const optimalCacString = formatValue({
      value: roundToNumDecimals(optimalCac).toString(),
      metricType: {
        type: "CURRENCY",
        currencyCode: currencyCode,
      },
      narrowCurrency: isNarrowCurrency(currencyCode),
    });

    const optimalCacProps = {
      title: intl.formatMessage({
        id: "Breakeven Customer Acquisition Cost",
        defaultMessage: "Breakeven Customer Acquisition Cost",
      }),
      type: "optimal-cac",
      summaryCards: [
        {
          title: intl.formatMessage({ id: "Consumers Tab: Breakeven CAC", defaultMessage: "Breakeven CAC" }),
          value: optimalCacString,
          background: false,
        },
      ],
      inputs: [
        {
          name: "Margin",
          label: intl.formatMessage({
            id: "Consumers Tab: Gross Profit Margin",
            defaultMessage: "Gross Profit Margin",
          }),
          value: margin,
          tooltip: intl.formatMessage({
            id: "Consumers Tab: Gross Profit Margin Tooltip",
            defaultMessage:
              "Gross profit margin is an analytical metric expressed as gross sales minus the cost of goods sold divided by gross sales",
          }),
          handleOnChange: handleMarginChange,
          unit: "%",
        },
        {
          name: "Months to breakeven",
          label: intl.formatMessage({
            id: "Consumers Tab: Months to breakeven",
            defaultMessage: "Months to breakeven",
          }),
          value: monthsProf,
          tooltip: intl.formatMessage({
            id: "Consumers Tab: Months to breakeven tooltip",
            defaultMessage: "The number of months required to breakeven",
          }),
          handleOnChange: handleMonthsChange,
          unit: "m",
        },
      ],
    };

    return {
      chartData,
      loading: false,
      summaryData: summaryPanelData,
      onSelectMetric: handleShowMetricChart,
      optimalCacProps,
    };
  };

  const generateLtvCacTabProps = () => {
    const tempRes = TemporalResOptions[selectedTempResIndex].id;

    const { columns, data, ltvQuartiles } = ltvCacData;

    const getToolTipForData = getDataTooltipFn(tempRes, getNumMonthMap(intl), currencyCode);

    if (enableStickyColumns) {
      columns.forEach((col) => {
        if (col.id === MetricTypes.FCP) {
          col.stickyParams = { align: "left", width: 91 };
        }
        if (col.id === MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT) {
          col.stickyParams = { align: "left", width: 91 };
        }
        if (col.id === MetricTypes.ALLTIME_CAC) {
          col.stickyParams = { align: "left", width: 91 };
        }
      });
    }

    return {
      headerRow: getHeaderProps(absoluteStart.month() + 1, absoluteStart.year()),
      ltvCacTab: {
        title: "Customer Lifetime Value vs. Customer Acquisition Cost",
        tooltip: LTV_CAC_SUMMARY,
        table: {
          columns,
          data,
          title: intl.formatMessage({
            id: "Consumers Tab: Customer value since first order",
            defaultMessage: "Customer value since first order",
          }),
          enablePagination: false,
          sortRows: sortRowsDefault,
          getRowStyle,
          getToolTipForColumn: ltvCacTableColumnTooltips,
          getToolTipForData,
          getStyleForData: getStyleForData(ltvQuartiles),
        },
      },
    };
  };
  if (needsUpdate) {
    const props: ConsumersScreenProps = {
      pageTitle: intl.formatMessage({
        id: "Consumers Tab: Consumers Lifetime value",
        defaultMessage: "Consumers Lifetime value",
      }),
      dataLoading: true,
    };

    if (dataError) {
      props.dataLoading = false;
      props.dataError = dataError;
    }

    if (summaryLTVData != null && ltvCacData != null && startDateString != null && endDateString != null) {
      const { headerRow, ltvCacTab } = generateLtvCacTabProps();

      props.headerRowProps = headerRow;
      // TODO update type of getToolTipForData, it can take more than a string
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      props.ltvTabProps = ltvCacTab;
      if (showLtvSummary) {
        props.summaryTabProps = generateLTVSummaryChartData(showLtvSummaryProjected);
      }
      props.dataLoading = false;
    }
    setNeedsUpdate(false);
    setConsumerProps(props);
  }

  const props = { ...consumerProps };

  const churnTabProps = useChurn(dashboardDataPath, selectedTempResIndex, startDateString, endDateString, setDataError);
  if (showChurnTab) {
    props["churnTabProps"] = churnTabProps;
  }

  return <ConsumersScreen {...props} />;
};

const ConnectedConsumers = () => {
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath } = getUrls(customerDomain);
  return (
    <CurrencyProvider dashboardDataPath={dashboardDataPath}>
      <Consumers dashboardDataPath={dashboardDataPath} />
    </CurrencyProvider>
  );
};

export default ConnectedConsumers;
